import { make, stopPropagationOnEditorJsControlKeys } from '@/editorjs/utils';
import { createColorPresetDropdown } from '@/editorjs/tools/shared/color-presets';
export default class Button {

  /**
   *
   * @returns {{icon: string, title: string}}
   */
  static get toolbox() {
    return {
      title: "Button",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <line x1="3" y1="12" x2="6" y2="12" />
      <line x1="12" y1="3" x2="12" y2="6" />
      <line x1="7.8" y1="7.8" x2="5.6" y2="5.6" />
      <line x1="16.2" y1="7.8" x2="18.4" y2="5.6" />
      <line x1="7.8" y1="16.2" x2="5.6" y2="18.4" />
      <path d="M12 12l9 3l-4 2l-2 4l-3 -9" />
    </svg>`
    }
  }

  /**
   * Returns true to notify the core that read-only mode is supported
   * @return {boolean}
   */
  static get isReadOnlySupported() {
    return true;
  }

  /**
   * @returns {boolean}
   */
  static get enableLineBreaks() {
    return false;
  }

  /**
   * @returns {{EDIT: string, VIEW: string}}
   * @constructor
   */
  static get STATE() {
    return {
      EDIT: 'edit',
      VIEW: 'view'
    };
  }

  /**
   * Allowed button alignments
   * @public
   * @returns {{left: string, center: string}}
   */
  static get ALIGNMENTS() {
    return {
      left: 'left',
      center: 'center',
      right: 'right',
      justify: 'justify'
    };
  }

  /**
   * Default button alignment
   * @public
   * @returns {string}
   */
  static get DEFAULT_ALIGNMENT() {
    return Button.ALIGNMENTS.center;
  }

  static get DEFAULT_TEXT_COLOR() {
    return "#FFFFFF"; // 기본 텍스트 색상을 흰색으로 변경하여 가독성 확보
  }

  static get DEFAULT_BACKGROUND_COLOR() {
    return "#3B82F6"; // 기본 배경색을 좀 더 눈에 띄는 색으로 변경
  }

  /**
   *
   * @param data
   */
  set data(data) {
    this._data = Object.assign({}, {
      link: this.api.sanitizer.clean(data.link || "", Button.sanitize),
      text: data.text,
      text_color: data.text_color || this.config.defaultTextColor || Button.DEFAULT_TEXT_COLOR,
      background_color: data.background_color || this.config.defaultBackgroundColor || Button.DEFAULT_BACKGROUND_COLOR,
      alignment: data.alignment || this.config.defaultAlignment || Button.DEFAULT_ALIGNMENT,
    });
  }

  /**
   *
   * @returns {{text: string, link: string}}
   */
  get data() {
    return this._data;
  }

  /**
   * 세이브 시의 유효성 검사
   * @param savedData - 저장될 데이터. 이 경우 this._data를 사용합니다.
   * @returns {boolean}
   */
  validate(savedData) {
    // 저장될 데이터(this._data)가 유효한지 확인합니다.
    if (!savedData.link || !savedData.text) {
      return false;
    }
    const isValidLink = /(https:\/\/|http:\/\/)(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/.test(savedData.link);
    return isValidLink;
  }

  /**
   *
   * @param block
   * @returns {{caption: string, text: string, alignment: string}}
   */
  save(block) {
    return this._data;
  }

  /**
   * 태그를 전부 삭제한다
   * @returns {{link: boolean, text: boolean}}
   */
  static get sanitize() {
    return {
      text: false, // 텍스트에 HTML 태그를 허용하지 않음
      link: false
    }
  }

  /**
   *
   * @param data
   * @param config
   * @param api
   * @param readOnly
   */
  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.config = config;
    this.readOnly = readOnly;

    // [변경] 현재 상태를 관리하기 위한 속성 추가
    this.state = Button.STATE.VIEW;
    this.uniqueId = `button-tool-${Math.random().toString(36).substring(2, 9)}`;

    this.nodes = {
      wrapper: null,
      container: null,
      inputHolder: null,
      buttonHolder: null,
      textInput: null,
      linkInput: null,
      textColorInput: null,
      backgroundColorInput: null,
      confirmButton: null, // registButton -> confirmButton 이름 변경
      button: null,
      alignmentButtons: {},
    }

    const _CSS = {
      baseClass: this.api.styles.block,
      hide: "button-tool-hide",
      btn: "btn",
      container: "button-tool-container",
      // [추가] 뷰 모드 및 호버 툴팁을 위한 클래스
      viewMode: 'button-tool--view',
      input: "button-tool-container__input",
      inputHolder: "button-tool-container__inputHolder",
      inputGridItem: "button-tool-container__inputGridItem",
      colorsHolder: "button-tool-container__colorsHolder",
      colorHolder: "button-tool-container__colorHolder",
      colorHolderLabel: "button-tool-container__colorHolder__label",
      colorSettingHolder: "button-tool-container__colorSettingHolder",
      colorSettingLink: "button-tool-container__colorSettingLink",
      inputText: "button-tool-container__input--text",
      inputLink: "button-tool-container__input--link",
      inputColor: "button-tool-container__input--color",
      confirmButtonHolder: "button-tool-container__confirmButtonHolder",
      confirmButton: "button-tool-container__confirmButton", // registButton -> confirmButton 클래스명 변경
      buttonHolder: "button-tool-container__buttonHolder",
      btnColor: "btn--default",
      inputLinkError: "button-tool-container__input--link--error",
      inputTextError: "button-tool-container__input--text--error",
      alignmentHolder: "button-tool-container__alignmentHolder",
      alignmentButton: "button-tool-container__alignmentButton",
      alignmentButtonActive: "button-tool-container__alignmentButton--active",
      alignment: {
        left: 'button-tool-container__buttonHolder--left',
        center: 'button-tool-container__buttonHolder--center',
        right: 'button-tool-container__buttonHolder--right',
        justify: 'button-tool-container__buttonHolder--justify',
      },
      inputHolderAttached: 'button-tool-container__inputHolder--attached',
    }

    this.CSS = Object.assign(_CSS, config.css)

    this.settings = [
      {
        name: 'left',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" id="Layer" enable-background="new 0 0 64 64" height="16" viewBox="0 0 64 64" width="16"><path d="m54 8h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m54 52h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m10 23h28c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/><path d="m54 30h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m10 45h28c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/></svg>`
      },
      {
        name: 'center',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" id="Layer" enable-background="new 0 0 64 64" height="16" viewBox="0 0 64 64" width="16"><path d="m54 8h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m54 52h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m46 23c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/><path d="m54 30h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m46 45c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/></svg>`
      },
      {
        name: 'right',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" id="Layer" enable-background="new 0 0 64 64" height="16" viewBox="0 0 64 64" width="16"><path d="m54 8h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m54 52h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m54 19h-28c-1.104 0-2 .896-2 2s.896 2 2 2h28c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m54 30h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/><path d="m54 41h-28c-1.104 0-2 .896-2 2s.896 2 2 2h28c1.104 0 2-.896 2-2s-.896-2-2-2z"/></svg>`
      },
      {
        name: 'justify',
        icon: `<svg viewBox="0 0 64 64" width="16" height="16"><path d="m54 8h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"></path><path d="m54 52h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"></path><path d="M 52.867 19 L 10.914 19 C 9.26 19 7.918 19.896 7.918 21 C 7.918 22.104 9.26 23 10.914 23 L 52.867 23 C 54.522 23 55.863 22.104 55.863 21 C 55.863 19.896 54.522 19 52.867 19 Z" style=""></path><path d="m54 30h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"></path><path d="M 52.779 41 L 11.113 41 C 9.469 41 8.136 41.896 8.136 43 C 8.136 44.104 9.469 45 11.113 45 L 52.779 45 C 54.421 45 55.754 44.104 55.754 43 C 55.754 41.896 54.421 41 52.779 41 Z" style=""></path></svg>`
      }
    ];

    this.data = {
      link: data.link || "",
      text: data.text || "",
      text_color: data.text_color || config.defaultTextColor || Button.DEFAULT_TEXT_COLOR,
      background_color: data.background_color || config.defaultBackgroundColor || Button.DEFAULT_BACKGROUND_COLOR,
      alignment: data.alignment || config.defaultAlignment || Button.DEFAULT_ALIGNMENT
    };
  }

  render() {
    this.nodes.container = make('div', [this.CSS.baseClass, this.CSS.container]);
    this.nodes.container.id = this.uniqueId;

    const initialState = this.validate(this._data) ? Button.STATE.VIEW : Button.STATE.EDIT;
    this.nodes.buttonHolder = this.makeButtonHolder();
    this.nodes.container.appendChild(this.nodes.buttonHolder);

    this.updateButtonView();
    this.changeState(initialState, true); // 두 번째 인자로 초기화 여부를 전달

    this.nodes.buttonHolder.addEventListener('click', (event) => {
      event.preventDefault();

      if (this.state === Button.STATE.VIEW && !this.readOnly) {
        this.changeState(Button.STATE.EDIT);
      }
    });

    window.addEventListener('click', this._handleClickOutside, true);

    return this.nodes.container;
  }

  destroy() {
    window.removeEventListener('click', this._handleClickOutside, true);
    this._removeInputHolderFromDom();
  }

  _handleClickOutside = (event) => {
    if (this.state !== Button.STATE.EDIT) {
      return;
    }
    // 클릭된 타겟이 내 컨테이너(버튼)나, body에 붙어있는 inputHolder 내부에 있지 않다면 닫기
    const clickedInsideContainer = this.nodes.container.contains(event.target);
    const clickedInsideInputHolder = this.nodes.inputHolder && this.nodes.inputHolder.contains(event.target);

    if (!clickedInsideContainer && !clickedInsideInputHolder) {
      this.saveCurrentData();
      if (this.validateTextInput(this.nodes.textInput.value) && this.validateLinkInput(this.nodes.linkInput.value)) {
        this.changeState(Button.STATE.VIEW);
      }
    }
  }

  makeInputHolder() {
    const inputHolder = make('div', [this.CSS.inputHolder]);
    // 텍스트 입력
    this.nodes.textInput = make('input', [this.api.styles.input, this.CSS.input, this.CSS.inputGridItem, this.CSS.inputText], {
      placeholder: this.api.i18n.t('버튼 텍스트'),
      value: this._data.text
    });

    this.nodes.textInput.addEventListener("keyup", this.onEditInput);

    // editor.js 오동작 방지
    this.nodes.textInput.addEventListener('keydown', this.onKeyDownInput);

    // 링크 입력
    this.nodes.linkInput = make('input', [this.api.styles.input, this.CSS.input, this.CSS.inputGridItem, this.CSS.inputLink], {
      placeholder: this.api.i18n.t('링크 URL (예: https://...)'),
      value: this._data.link
    })

    this.nodes.linkInput.addEventListener("keyup", this.onEditInput);
    // editor.js 오동작 방지
    this.nodes.linkInput.addEventListener('keydown', this.onKeyDownInput);

    // 색상 선택기
    const colorsHolder = make('div', [this.CSS.inputGridItem, this.CSS.colorsHolder]);
    // 텍스트 색상
    const textColorHolder = make('div', [this.CSS.colorHolder]);
    const textColorLabel = make('p', [this.CSS.colorHolderLabel]);
    this.nodes.textColorInput = make('input', [this.CSS.inputColor], {
      type: "color",
      value: this._data.text_color
    })
    this.nodes.textColorInput.addEventListener('input', this.onEditInput);
    textColorLabel.innerText = this.api.i18n.t('글자색');
    textColorHolder.appendChild(textColorLabel)
    textColorHolder.appendChild(this.nodes.textColorInput)

    // 배경 색상
    const backgroundColorHolder = make('div', [this.CSS.colorHolder]);
    const backgroundColorLabel = make('p', [this.CSS.colorHolderLabel]);
    this.nodes.backgroundColorInput = make('input', [this.CSS.inputColor], {
      type: "color",
      value: this._data.background_color
    })
    this.nodes.backgroundColorInput.addEventListener('input', this.onEditInput);
    backgroundColorLabel.innerText = this.api.i18n.t('배경색');
    backgroundColorHolder.appendChild(backgroundColorLabel)
    backgroundColorHolder.appendChild(this.nodes.backgroundColorInput)

    colorsHolder.appendChild(textColorHolder);
    colorsHolder.appendChild(backgroundColorHolder);

    // 프리셋 팔레트 (드롭다운)
    const presetDropdown = createColorPresetDropdown((textColor, bgColor) => {
      this.nodes.textColorInput.value = textColor;
      this.nodes.backgroundColorInput.value = bgColor;
      this.onEditInput();
    }, { text: this.config.defaultTextColor, bg: this.config.defaultBackgroundColor });
    colorsHolder.appendChild(presetDropdown);

    // 설정 아이콘 (맨 오른쪽, 팔레트 아이콘과 동일 스타일)
    if (this.config.colorSettingLink) {
      const colorSettingLink = document.createElement('a');
      colorSettingLink.href = this.config.colorSettingLink;
      colorSettingLink.target = '_blank';
      colorSettingLink.rel = 'nofollow noindex noreferrer';
      colorSettingLink.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" /><circle cx="12" cy="12" r="3" /></svg>`;
      colorSettingLink.style.cssText = "width: 34px; height: 34px; border: none; background: none; cursor: pointer; color: #1e293b; transition: color 0.15s; flex-shrink: 0; display: flex; align-items: center; justify-content: center; padding: 0; text-decoration: none;";
      const isDark = document.documentElement.classList.contains('dark');
      if (isDark) colorSettingLink.style.color = "#a3a3a3";
      const settingHoverColor = isDark ? "#d1d5db" : "#000000";
      const settingDefaultColor = isDark ? "#a3a3a3" : "#1e293b";
      colorSettingLink.addEventListener("mouseenter", () => { colorSettingLink.style.color = settingHoverColor; });
      colorSettingLink.addEventListener("mouseleave", () => { colorSettingLink.style.color = settingDefaultColor; });
      colorsHolder.appendChild(colorSettingLink);
    };

    const alignmentHolder = make('div', [this.CSS.inputGridItem, this.CSS.alignmentHolder]);
    this.settings.forEach(tune => {
      const button = make('button', [this.CSS.alignmentButton], {
        type: 'button',
      });
      button.innerHTML = tune.icon;
      button.addEventListener('click', () => {
        this._setAlignment(tune.name);
      });

      // 생성된 버튼을 nodes에 저장하여 나중에 참조할 수 있도록 함
      this.nodes.alignmentButtons[tune.name] = button;
      alignmentHolder.appendChild(button);
    });

    inputHolder.appendChild(this.nodes.textInput);
    inputHolder.appendChild(this.nodes.linkInput);
    inputHolder.appendChild(alignmentHolder); // [추가] 정렬 홀더 추가
    inputHolder.appendChild(colorsHolder);

    return inputHolder;
  }

  // [변경] 입력이 있을 때마다 데이터 객체를 업데이트하고 버튼 미리보기를 갱신하는 메소드
  onEditInput = () => {
    this.saveCurrentData();
    this.updateButtonView();
  }

  onKeyDownInput = (event) => {
    stopPropagationOnEditorJsControlKeys(event);

    if (event.code === 'Enter') {
      event.preventDefault();
    }
  }

  // [추가] 현재 입력된 값들을 _data 객체에 저장하는 헬퍼 메소드
  saveCurrentData() {
    this.validateTextInput(this.nodes.textInput.value);
    this.validateLinkInput(this.nodes.linkInput.value);

    this.data = {
      link: this.nodes.linkInput.value,
      text: this.nodes.textInput.value,
      text_color: this.nodes.textColorInput.value,
      background_color: this.nodes.backgroundColorInput.value,
      alignment: this.data.alignment
    }
  }

  validateLinkInput(urlString) {
    const isValidLink = /(https:\/\/|http:\/\/)(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/.test(urlString);
    this.nodes.linkInput.classList.toggle(this.CSS.inputLinkError, !isValidLink);
    return isValidLink;
  }

  validateTextInput(textString) {
    const isValidText = (textString !== null && textString !== "");
    this.nodes.textInput.classList.toggle(this.CSS.inputTextError, !isValidText);
    return isValidText;
  }

  // [변경] 버튼의 시각적 표현을 업데이트하는 역할만 수행
  updateButtonView() {
    this.nodes.button.textContent = this._data.text || this.api.i18n.t("버튼");
    this.nodes.button.setAttribute("href", this._data.link);
    this.nodes.button.style.background = this._data.background_color;
    this.nodes.button.style.color = this._data.text_color;

    // 정렬 클래스 업데이트
    Object.values(this.CSS.alignment).forEach(className => {
      this.nodes.buttonHolder.classList.remove(className);
    });
    this.nodes.buttonHolder.classList.add(this.CSS.alignment[this._data.alignment]);
  }

  makeButtonHolder() {
    const buttonHolder = make('div', [this.CSS.buttonHolder, this.CSS.alignment[this.data.alignment]]);
    this.nodes.button = make('a', [this.CSS.btn, this.CSS.btnColor], {
      target: '_blank',
      rel: 'nofollow noindex noreferrer',
    });
    this.nodes.button.textContent = this.api.i18n.t("버튼");
    buttonHolder.appendChild(this.nodes.button);
    return buttonHolder;
  }

  _removeInputHolderFromDom() {
    if (this.nodes.inputHolder && this.nodes.inputHolder.parentNode) {
      this.nodes.inputHolder.parentNode.removeChild(this.nodes.inputHolder);
    }
    this.nodes.inputHolder = null;
  }

  changeState(newState, isInitializing = false) {
    // 이미 같은 상태라면 아무것도 하지 않음 (초기화 시 제외)
    if (!isInitializing && this.state === newState) {
      return;
    }

    this.state = newState;

    if (this.state === Button.STATE.EDIT) {
      if (!this.nodes.inputHolder) {
        this.nodes.inputHolder = this.makeInputHolder();
      }

      // inputHolder를 body에 붙이고 위치 계산
      this.attachInputHolderToBody();

      this.nodes.container.classList.remove(this.CSS.viewMode);
    } else { // VIEW 모드
      this._removeInputHolderFromDom();

      this.updateButtonView();
      this.nodes.container.classList.add(this.CSS.viewMode);
    }
  }

  // [핵심 변경] inputHolder를 body에 붙이고 위치를 조정하는 새 메서드
  attachInputHolderToBody() {
    if (!this.nodes.inputHolder) return;

    // 1. inputHolder를 body에 먼저 추가해야 정확한 너비(offsetWidth)를 계산할 수 있습니다.
    this.nodes.inputHolder.classList.add(this.CSS.inputHolderAttached);
    document.body.appendChild(this.nodes.inputHolder);

    requestAnimationFrame(() => {

      // 2. 기준이 될 '버튼'과 위치를 조절할 'inputHolder'의 크기와 위치 정보를 가져옵니다.
      const buttonRect = this.nodes.button.getBoundingClientRect();
      const holderRect = this.nodes.inputHolder.getBoundingClientRect();

      if (buttonRect.width === 0) return;

      const left = ((buttonRect.left - (window.innerWidth / 2)) * 2) + (buttonRect.width);
      const top = buttonRect.bottom + window.scrollY + 5;

      this.nodes.inputHolder.style.top = `${top}px`;
      this.nodes.inputHolder.style.left = `${left}px`;

      // Alignment 버튼 상태 업데이트
      this._updateAlignmentButtonsView();
    });
  }

  get shortcut() {
    return 'CMD+SHIFT+A';
  }

  /**
   * 폼의 정렬 버튼 클릭을 처리합니다.
   * @private
   */
  _setAlignment(alignmentName) {
    // 1. 데이터 업데이트
    this.data.alignment = alignmentName;

    // 2. 버튼 미리보기 업데이트
    this.updateButtonView();

    // 3. 폼 내부의 정렬 버튼들 활성 상태 업데이트
    this._updateAlignmentButtonsView();
  }

  /**
   * 폼에 있는 정렬 버튼들의 시각적 상태(활성/비활성)를 업데이트합니다.
   * @private
   */
  _updateAlignmentButtonsView() {
    for (const name in this.nodes.alignmentButtons) {
      const button = this.nodes.alignmentButtons[name];
      const isActive = name === this.data.alignment;
      button.classList.toggle(this.CSS.alignmentButtonActive, isActive);
    }
  }
}